import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { ModalContext, ScheduleContext } from "src/context";
import { calendar, monitor, phone_call } from "src/images/NewDesign";
import { sanitizeURL } from "src/utils/misc";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { MiniCalendar } from "./MiniCalendar";

interface ICalendar {
  eventData: {
    id: string;
    title: string;
    lead_id: string;
    start: string;
    end: string;
    type: string;
    event_title: string;
    event_description: string;
  }[];
}

export const Calendar: React.FC<ICalendar> = ({ eventData }) => {
  const calendarRef = useRef(null);

  const { setMoveEventData, setGeneralTimesEventsModal } = useContext(ModalContext);
  const { setMyScheduleDate } = useContext(ScheduleContext);

  useEffect(() => {
    // manually resize after 0.5s. This will allow the calendar to adjust column sizing after queue animation plays
    const timer = setTimeout(() => {
      const calendarApi = (calendarRef.current as any)?.getApi();
      if (calendarApi) {
        calendarApi.render();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (calendarRef.current) {
      // Inject month menu component into the fc-timegrid-axis cell
      const axisCell = document.querySelector(".fc-timegrid-axis");
      if (axisCell) {
        axisCell.innerHTML = ""; // Clear any existing content
        const container = document.createElement("div");
        ReactDOM.render(<AxisCell calendarRef={calendarRef} />, container);
        axisCell.appendChild(container);
      }

      // There isnt enough customization for the time column labels in FullCalendar.
      // We need to manually add our own label.
      const slotLabelFrame = document.querySelectorAll(".fc-timegrid-slot");
      slotLabelFrame.forEach((el) => {
        const p = document.createElement("p");
        const slotTime = el.getAttribute("data-time");
        if (slotTime) {
          const formattedTime = moment(slotTime, "HH:mm:ss").format("h:mm A");
          p.textContent = formattedTime;
          el.firstElementChild?.appendChild(p);
        }
      });
    }
  }, [calendarRef]);

  const eventContent = useCallback(
    (arg) => {
      const { event } = arg;
      const title = event.title;
      const type = event.extendedProps.type;
      const allDay = event.allDay;

      return (
        <EventBlock
          title={title}
          type={type}
          eventId={event.id}
          allDay={allDay}
          setGeneralTimesEventsModal={setGeneralTimesEventsModal}
        />
      );
    },
    [setGeneralTimesEventsModal],
  );

  return (
    <CalendarWrap id="cal-wrap">
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        selectable={false}
        editable={true}
        nowIndicator={true} // Shows a red line for the current time
        nowIndicatorContent={(arg) => {
          // don't show on the main calandar red line
          if (!arg.isAxis) return null;

          //Example: 5:00 PM
          const time = moment(arg.date).format("h:mm A");
          return <NowIndicatorTime>{time}</NowIndicatorTime>;
        }}
        events={eventData}
        eventContent={eventContent}
        eventDrop={(info) => {
          const updatedEvent = {
            id: info.event.id,
            leadId: info.event.extendedProps.lead_id,
            type: info.event.extendedProps.type,
            eventTitle: info.event.extendedProps.event_title,
            eventDescription: info.event.extendedProps.event_description,
            start: info.event.start as Date,
            end: info.event.end as Date,
            revert: info.revert,
          };

          setMoveEventData(updatedEvent);
        }}
        eventDurationEditable={false}
        eventAllow={(dropInfo, event) => {
          // dont allow regular events to be dragged into the all day row
          // also dont allow allDay events to be dragged
          return !dropInfo.allDay && !event?.allDay;
        }}
        eventClick={(info) => setMyScheduleDate(moment(info.event.start).format("YYYY-MM-DD"))}
        height="100%"
        headerToolbar={false}
        dayHeaderContent={DayHeaderContent}
        allDayText="General Callback"
        slotLabelFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        }}
        scrollTime={moment.max(moment().startOf("day"), moment().clone().subtract(1, "hours")).format("HH:mm:ss")}
      />
    </CalendarWrap>
  );
};

const DayHeaderContent = (arg: any) => {
  const today = moment().format("YYYY-MM-DD");
  const isToday = moment(arg.date).format("YYYY-MM-DD") === today;

  return (
    <HeaderCell>
      <AppText
        fontSize={12}
        fontWeight={600}
        uppercase
        letterSpacing={1}
        lineHeight={18}
        color={theme.text.neutral.secondary}
      >
        {moment(arg.date).format("ddd")}
      </AppText>
      <AppText
        fontSize={16}
        lineHeight={22}
        color={isToday ? theme.text.brand.primary : theme.text.neutral.primary}
        fontWeight={500}
      >
        {moment(arg.date).format("D")}
      </AppText>
    </HeaderCell>
  );
};

const AxisCell = ({ calendarRef }: { calendarRef: any }) => {
  const curMonth = useMemo(() => moment().format("MMM"), []);
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);

  const onDropdownClick = useCallback(() => {
    setShowMiniCalendar(true);
  }, []);
  const onClickOutside = useCallback(() => {
    setShowMiniCalendar(false);
  }, []);

  const handleTimeframeSwitch = useCallback(
    (switchTo: string) => {
      const curView = calendarRef.current?.getApi()?.view?.type;
      if (!curView) return;
      calendarRef.current?.getApi()?.changeView(switchTo);

      const weekBacking: HTMLDivElement | null = document.querySelector(".WeekBacking");
      if (weekBacking) {
        // hide week backing on day view
        weekBacking.style.opacity = switchTo === "timeGridWeek" ? "1" : "0";
      }

      const todaySyncInner: HTMLElement | null = document.querySelector(".fc-day-today > .fc-scrollgrid-sync-inner");
      if (todaySyncInner) {
        todaySyncInner.style.left = switchTo === "timeGridWeek" ? "50%" : "5%";
      }
    },
    [calendarRef],
  );

  const handleDaySwitch = useCallback(
    (newDate: string) => {
      const api = calendarRef.current?.getApi();
      api.gotoDate(newDate);
    },
    [calendarRef],
  );
  const getDate = useCallback(() => calendarRef.current?.getApi()?.getDate(), [calendarRef]);

  return (
    <div style={{ width: "64px", padding: "6px 0px 0px 16px" }}>
      {showMiniCalendar && (
        <MiniCalendar
          onClickOutside={onClickOutside}
          handleTimeframeSwitch={handleTimeframeSwitch}
          curView={calendarRef.current?.getApi()?.view?.type}
          handleDaySwitch={handleDaySwitch}
          getDate={getDate}
        />
      )}
      <PhoenixMultiSelect
        isMulti={false}
        isClearable={false}
        name="month"
        options={[]}
        value={[{ label: curMonth, value: null }]}
        menuPortal
        marginBottom={false}
        ghostSelect
        fontSize="18px"
        width={64}
        height={32}
        minHeight={32}
        disableHoverColor
        pointer
        onClick={onDropdownClick}
        isDisabled
      />
    </div>
  );
};

const CalendarWrap = styled.div`
  position: relative;

  width: 100%;
  height: 100%;

  background-color: ${theme.fill.brand.tertiary};
  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 8px;

  overflow: hidden;

  table {
    border: none !important;
  }

  // Calendar Columns
  .fc-col-header col,
  .fc-scrollgrid-sync-table col,
  .fc-timegrid-slots col,
  .fc-timegrid-cols col {
    width: 80px !important;
  }

  // Header
  .fc-col-header {
    width: 100% !important;
  }
  thead .fc-scroller,
  tbody .fc-scrollgrid-section:first-child .fc-scroller {
    overflow: hidden !important;
  }
  .fc-scrollgrid-section-body > td {
    border: none !important;
  }
  .fc-scrollgrid-section-header > th {
    border: none;
  }
  th .fc-day-today > .fc-scrollgrid-sync-inner {
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
  }
  .fc-col-header-cell {
    position: relative;
  }
  .fc-col-header-cell,
  thead .fc-timegrid-axis {
    padding-top: 4px;
    border-width: 0px 0px 1px 0px;
    border-bottom: 1px solid ${theme.border.neutral.secondary};
  }
  .fc thead tr {
    background-color: ${theme.WHITE_COLOR};
    height: 58px;
  }
  thead .fc-timegrid-axis-frame {
    display: none !important;
  }

  // Calendar Body
  .fc-timegrid-divider {
    display: none !important;
  }
  .fc .fc-col-header-cell-cushion {
    padding: 0px;
  }
  .fc-timegrid-body tr {
    height: 28px;
  }
  .fc-timegrid-body .fc-timegrid-slots tr td:first-child {
    border: none;
  }
  .fc .fc-timegrid-now-indicator-container:first-child {
    border-right: 1px solid ${theme.border.neutral.secondary};
  }
  .fc-timegrid-col:first-child {
    border: none;
  }
  .fc-timegrid-cols .fc-timegrid-axis {
    background-color: ${theme.fill.neutral.primary};
  }
  .fc-timegrid-cols .fc-day {
    background-color: ${theme.fill.brand.tertiary};
  }

  // white border on all cells
  .fc-timegrid-cols td {
    border: 1px solid ${theme.WHITE_COLOR};
    border-width: 0px 1px 1px 1px;
  }
  .fc-timegrid-slot {
    border: 1px solid ${theme.WHITE_COLOR};
    border-width: 0px 1px 1px 1px;
  }

  // All-Day Row
  .fc-daygrid-body tr {
    height: 56px;
    background-color: ${theme.fill.brand.tertiary};
  }
  .fc-daygrid-body tr td {
    border-width: 0px 1px 1px 1px;
    border: 1px solid ${theme.WHITE_COLOR};
  }
  .fc .fc-timegrid-axis-frame {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    padding: 4px;

    background-color: ${theme.fill.neutral.primary};
    border-right: 1px solid ${theme.border.neutral.secondary};
  }
  .fc .fc-timegrid-axis-frame span {
    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 12px;
    text-align: right;
    color: ${theme.text.neutral.secondary};

    width: 52px;
  }

  // Time Column Overrides
  .fc .fc-timegrid-slot-label-cushion {
    display: none !important;
  }
  .fc-timegrid-slot-label-frame > p {
    width: 23px;
    margin-left: auto;
    margin-right: 4px;

    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 12px;
    text-align: right;
    color: ${theme.text.neutral.secondary};

    word-break: break-all;
    white-space: normal;
  }

  // Current Day Cells
  .fc-timegrid-cols .fc-day-today,
  .fc-daygrid-body .fc-day-today {
    background-color: ${theme.fill.brand.secondary};
  }

  // Event Blocks
  .fc-v-event {
    background: transparent;
    border: none;
  }
  .fc-event {
    background: transparent;
    border: none;
  }
`;

const HeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  font-weight: bold;
`;

const NowIndicatorTime = styled(AppText)`
  margin-left: 4px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  color: ${theme.text.danger.primary};
  font-size: 10px;
  font-weight: 400;
`;

const EventBlock = ({
  title,
  type,
  eventId,
  allDay,
  setGeneralTimesEventsModal,
}: {
  title: string;
  type: string;
  eventId: string;
  allDay: boolean;
  setGeneralTimesEventsModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const isExternal = useMemo(() => eventId.startsWith("external"), [eventId]);

  const eventIcon = useMemo(() => {
    switch (type) {
      case "Demo":
        return monitor;
      case "ScheduledCallBack":
        return calendar;
      case "CallBack":
        return calendar;
      case "GeneralCallBack":
        return phone_call;
      default:
        return undefined;
    }
  }, [type, isExternal]);

  const eventColor = useMemo(() => {
    switch (type) {
      case "Demo":
        return {
          border: theme.border.warning.primary,
          background: theme.WARNING400,
          icon: theme.icon.warning.hover,
        };
      case "ScheduledCallBack":
        return {
          border: theme.border.dataviz2.primary,
          background: theme.fill.dataviz2.secondary,
          icon: theme.icon.dataviz2.secondary,
        };
      case "CallBack":
        return {
          border: theme.border.dataviz2.primary,
          background: theme.fill.dataviz2.secondary,
          icon: theme.icon.dataviz2.secondary,
        };
      case "GeneralCallBack":
        return {
          border: theme.border.dataviz2.secondary,
          background: theme.fill.dataviz2.disabled,
          icon: theme.icon.dataviz2.default,
        };
      default:
        if (isExternal)
          return {
            border: theme.border.neutral.primary,
            background: "rgba(255, 255, 255, 0.5)",
            icon: theme.icon.neutral.default,
          };
        return {
          border: theme.border.success.primary,
          background: theme.fill.success.secondary,
          icon: theme.icon.success.default,
        };
    }
  }, [type, isExternal]);

  const handleEventClick = useCallback(() => {
    if (allDay) {
      setGeneralTimesEventsModal(true);
      return;
    }
    const santizedURL = sanitizeURL(`/manage-schedule/${eventId}`);
    window.open(santizedURL, "_blank");
  }, [eventId, allDay]);

  return (
    <EventContainer
      type={type}
      style={{ backgroundColor: eventColor.background, border: `1px solid ${eventColor.border}` }}
      onClick={handleEventClick}
    >
      <FlexDiv align="center" height={16}>
        <AppText
          fontSize={10}
          fontWeight={500}
          lineHeight={14}
          noWrap
          style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
          color={isExternal ? theme.text.neutral.disabled : theme.text.neutral.primary}
        >
          {!isExternal ? title : "External Event"}
        </AppText>
        {type && <PhoenixIcon svg={eventIcon} size={12} color={eventColor.icon} hoverColor={eventColor.icon} />}
      </FlexDiv>
    </EventContainer>
  );
};

const EventContainer = styled.div<{ type: string }>`
  width: 100%;
  height: 100%;

  padding: 4px 8px;

  border-radius: 4px;
  background-color: ${theme.fill.brand.secondary};
`;
